/**
 * useModuleUnlock — Determines unlock status for each module in the catalog.
 *
 * Reads user's merit balance, session count, and tier.
 * Returns unlock state per module: isUnlocked, progress (0–1), reason.
 *
 * NOTE: user_module_activations table needed for manual unlocks.
 * RLS policy required — flagged but not created here (frozen zone).
 *
 * @since Module Discovery System v1
 */

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useUserPoints } from "@/hooks/useUserPoints";
import { useUserTier } from "@/hooks/useUserTier";
import { MODULES_CATALOG, type ModuleDefinition, type ModuleStatus } from "@/constants/modulesCatalog";
import { FEATURE_FLAGS } from "@/constants/featureFlags";
import { isFlagAccessibleForTier, getRequiredTierForFlag, TIER_CONFIG, type PerformTier } from "@/constants/tiers";

export interface ModuleUnlockState {
  moduleId: string;
  status: ModuleStatus;
  isUnlocked: boolean;
  progress: number; // 0–1
  reason: string;
  /** True when module is locked because user's tier is insufficient */
  tierLocked?: boolean;
  /** The tier required to access this module (only set when tierLocked) */
  requiredTier?: PerformTier;
}

export function useModuleUnlock() {
  const { user } = useAuth();
  const { balance } = useUserPoints();
  const { performTier } = useUserTier();

  // Count completed sessions (checkins)
  const { data: sessionCount = 0 } = useQuery({
    queryKey: ["module-unlock-sessions", user?.id],
    queryFn: async () => {
      if (!user) return 0;
      const { count } = await supabase
        .from("program_checkins" as any)
        .select("id", { count: "exact", head: true })
        .eq("user_id", user.id)
        .eq("completed", true);
      return count ?? 0;
    },
    enabled: !!user,
    staleTime: 2 * 60_000,
  });

  // Manual activations from user_module_activations
  const { data: activations = [] } = useQuery({
    queryKey: ["module-activations", user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from("user_module_activations")
        .select("module_id")
        .eq("user_id", user.id);
      if (error) {
        if (import.meta.env.DEV) console.warn("[modules] activations query error:", error.message);
        return [];
      }
      return data?.map((d) => d.module_id) ?? [];
    },
    enabled: !!user,
    staleTime: 2 * 60_000,
  });

  const moduleStates = useMemo(() => {
    const states: Record<string, ModuleUnlockState> = {};

    for (const mod of MODULES_CATALOG) {
      const flagKey = mod.flag as keyof typeof FEATURE_FLAGS;
      const flagEnabled = FEATURE_FLAGS[flagKey] ?? false;

      // Future modules
      if (mod.section === "future" || !flagEnabled) {
        states[mod.id] = {
          moduleId: mod.id,
          status: "coming_soon",
          isUnlocked: false,
          progress: 0,
          reason: "Bientôt disponible",
        };
        continue;
      }

      // ── Tier access check ──
      // If flag exists but user's tier is insufficient → tier-locked
      if (!isFlagAccessibleForTier(flagKey, performTier)) {
        const requiredTier = getRequiredTierForFlag(flagKey);
        const tierLabel = TIER_CONFIG[requiredTier]?.label ?? requiredTier;
        states[mod.id] = {
          moduleId: mod.id,
          status: "locked",
          isUnlocked: false,
          progress: 0,
          reason: `Disponible avec le plan ${tierLabel}`,
          tierLocked: true,
          requiredTier,
        };
        continue;
      }

      // Advanced section modules — always available in advanced mode
      if (mod.section === "advanced") {
        states[mod.id] = {
          moduleId: mod.id,
          status: "active",
          isUnlocked: true,
          progress: 1,
          reason: "Disponible",
        };
        continue;
      }

      // Extra section — check unlock conditions
      if (!mod.unlockCondition) {
        states[mod.id] = {
          moduleId: mod.id,
          status: "available",
          isUnlocked: true,
          progress: 1,
          reason: "Disponible",
        };
        continue;
      }

      // Manual activation check
      if (activations.includes(mod.id)) {
        states[mod.id] = {
          moduleId: mod.id,
          status: "active",
          isUnlocked: true,
          progress: 1,
          reason: "Activé",
        };
        continue;
      }

      const cond = mod.unlockCondition;

      if (cond.type === "merits" && cond.value) {
        const progress = Math.min(balance / cond.value, 1);
        const met = balance >= cond.value;
        states[mod.id] = {
          moduleId: mod.id,
          status: met ? "available" : "locked",
          isUnlocked: met,
          progress,
          reason: met ? "Prêt à activer" : cond.label,
        };
      } else if (cond.type === "sessions" && cond.value) {
        const progress = Math.min(sessionCount / cond.value, 1);
        const met = sessionCount >= cond.value;
        states[mod.id] = {
          moduleId: mod.id,
          status: met ? "available" : "locked",
          isUnlocked: met,
          progress,
          reason: met ? "Prêt à activer" : cond.label,
        };
      } else {
        states[mod.id] = {
          moduleId: mod.id,
          status: "locked",
          isUnlocked: false,
          progress: 0,
          reason: cond.label,
        };
      }
    }

    return states;
  }, [balance, sessionCount, activations, performTier]);

  const activeCount = useMemo(
    () => Object.values(moduleStates).filter((s) => s.status === "active").length,
    [moduleStates]
  );

  return { moduleStates, activeCount };
}
